import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import {
  boardColumnForStatus,
  editTask,
  fetchPulseTaskStatuses,
  nextTaskStatus,
  setTaskStatus,
  type RoomTask,
} from '~/data/interact'
import type { Person } from '~/data/types'
import { theme } from '~/theme'
import { AgentCharacter, showsCharacter } from './AgentAvatar'

// One task, in full: edit title/description/due date, cycle status, toggle
// assignees — roughly Nova's task sheet, flattened to the office's idiom.

const STATUS_LABELS: Record<string, string> = {
  TODO: 'Open',
  INPROGRESS: 'Doing',
  COMPLETED: 'Done',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
}

const statusColor = (s: string): string =>
  s === 'COMPLETED'
    ? '#54d18c'
    : s === 'INPROGRESS'
      ? '#5b8dd6'
      : s === 'OVERDUE'
        ? '#ff5a5a'
        : '#e0a13a'

export function TaskDetailPanel({
  task,
  pulseId,
  organizationId,
  members,
  getToken,
  onClose,
}: {
  task: RoomTask
  pulseId: string
  organizationId: string
  members: Person[]
  getToken: GetToken
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.description ?? '')
  const [dueDate, setDueDate] = useState((task.dueDate ?? '').slice(0, 10))
  const [assignees, setAssignees] = useState<string[]>(
    task.assignees.map((a) => a.userId),
  )
  const [status, setStatus] = useState(task.status)
  const [note, setNote] = useState<string | null>(null)
  const client = async () => createClient(await getToken())

  const boardColumns = useQuery({
    queryKey: ['taskColumns', pulseId],
    queryFn: async () => fetchPulseTaskStatuses(await client(), pulseId),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  })

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['roomTasks', pulseId] })

  const save = useMutation({
    mutationFn: async () => {
      await editTask(await client(), {
        organizationId,
        taskId: task.id,
        title: title.trim() || task.title,
        description: description.trim() || null,
        dueDate: dueDate || null,
        assignees,
      })
    },
    onSuccess: () => {
      setNote('Saved ✓')
      setTimeout(() => setNote(null), 2000)
      refresh()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 120) : 'Save failed'),
  })

  const cycle = useMutation({
    mutationFn: async () => {
      const next = nextTaskStatus(status)
      const column = boardColumnForStatus(boardColumns.data ?? [], next)
      await setTaskStatus(await client(), {
        organizationId,
        taskId: task.id,
        status: next,
        taskStatusId: column?.id ?? null,
      })
      return next
    },
    onSuccess: (next) => {
      if (next) setStatus(next)
      refresh()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 120) : 'Status change failed'),
  })

  const toggleAssignee = (userId: string) =>
    setAssignees((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    )

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => cycle.mutate()}
            disabled={cycle.isPending}
            title="Click to move status"
            style={{
              ...statusChip,
              color: statusColor(status),
              borderColor: `${statusColor(status)}66`,
            }}
          >
            {STATUS_LABELS[status] ?? status}
          </button>
          {task.number && (
            <span style={{ fontSize: 10.5, color: theme.textDim }}>#{task.number}</span>
          )}
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ ...input, marginTop: 8, fontSize: 14, fontWeight: 600 }}
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description…"
          rows={5}
          style={{ ...input, marginTop: 6, resize: 'vertical', lineHeight: 1.5 }}
        />

        <div style={sectionLabel}>Due date</div>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          style={{ ...input, width: 160, colorScheme: 'dark' }}
        />

        <div style={sectionLabel}>Assignees</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {members.map((m) => {
            const on = assignees.includes(m.userId)
            return (
              <button
                key={m.userId}
                onClick={() => toggleAssignee(m.userId)}
                style={{
                  ...memberChip,
                  ...(on
                    ? { color: theme.cream, borderColor: theme.terracotta, background: '#2a1d18' }
                    : null),
                }}
              >
                {/* A pictureless agent's character in place of the ✦. */}
                {showsCharacter(m) ? (
                  <AgentCharacter
                    userId={m.userId}
                    size={14}
                    style={{ display: 'inline-block', verticalAlign: '-3px', marginRight: 4 }}
                  />
                ) : m.isAgent ? (
                  '✦ '
                ) : (
                  ''
                )}
                {m.name.split(' ')[0]}
              </button>
            )
          })}
        </div>

        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !title.trim()}
          style={saveBtn}
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {note && (
          <div
            style={{
              fontSize: 11,
              marginTop: 6,
              textAlign: 'center',
              color: note === 'Saved ✓' ? '#54d18c' : '#ff8a7a',
            }}
          >
            {note}
          </div>
        )}
      </div>
    </div>
  )
}

const backdrop: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: '#140d07c9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}
const card: React.CSSProperties = {
  width: 'min(440px, 92vw)',
  maxHeight: '84vh',
  overflowY: 'auto',
  padding: 16,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 14,
  boxShadow: '0 24px 80px #000c',
  color: theme.text,
}
const statusChip: React.CSSProperties = {
  background: 'none',
  border: '1px solid',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  padding: '4px 12px',
  cursor: 'pointer',
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.text,
  borderRadius: 9,
  fontSize: 12.5,
  padding: '9px 10px',
  outline: 'none',
}
const sectionLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  color: theme.textDim,
  margin: '12px 0 5px',
}
const memberChip: React.CSSProperties = {
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 999,
  fontSize: 11,
  padding: '5px 10px',
  cursor: 'pointer',
}
const saveBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 14,
  background: theme.terracotta,
  border: 'none',
  color: '#fff',
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 700,
  padding: '10px 0',
  cursor: 'pointer',
}
